import { MouseEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVenueVideoActive } from '../../actions/venues-card-video';
import { ReactComponent as PauseButton } from '../../assets/icons/pause-icon.svg';
import { ReactComponent as PlayButton } from '../../assets/icons/play-icon.svg';
import { useElementOnScreen } from '../../hooks/useElementOnScreen';
import { IState } from '../../models/reducers/i-state.interface';
import styles from './VenueBackground.module.scss';
import VideoBanner from './VideoBanner';

interface IVenueBackgroundProps {
  backgroundImageUrl?: string | null;
  backgroundAnimationUrl?: string | null;
  className?: string;
  objectFit?: 'cover' | 'contain';
  children?: ReactNode;
  onMini?: boolean;
  autoPlay?: boolean;
  lazy?: boolean;
}

const VenueBackground = ({
  backgroundImageUrl,
  backgroundAnimationUrl,
  children,
  className = '',
  objectFit = 'cover',
  onMini = false,
  autoPlay = false,
  lazy = false,
}: IVenueBackgroundProps) => {
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  // CSS background-image has no native lazy loading, so off-screen cards would fetch
  // full-res images immediately; defer setting the URL until the element is visible.
  const isOnScreen = useElementOnScreen(containerRef, 0, true);
  const showBackground = !lazy || isOnScreen;
  const dispatch = useDispatch();
  const { activeVideo } = useSelector(({ venuesCardVideo }: IState) => venuesCardVideo);
  const buttonClassNames = [styles['video-button'], onMini ? styles['video-button-mini'] : ''].join(' ').trim();
  const classNames = [
    className,
    styles['venue-background-container'],
    styles[`venue-background-container--${objectFit}`],
  ];
  const isBackgroundVideo = backgroundAnimationUrl?.endsWith('.mp4');
  const backgroundStyle = {
    ...(showBackground && { backgroundImage: `url("${backgroundImageUrl}")` }),
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
  };

  useEffect(() => {
    if (!autoPlay) {
      return;
    }

    dispatch(setVenueVideoActive(id));
  }, [autoPlay, backgroundAnimationUrl]);

  const playPauseVideo = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (id === activeVideo) {
      dispatch(setVenueVideoActive(''));

      return;
    }

    dispatch(setVenueVideoActive(id));
  };

  return (
    <div ref={containerRef} style={backgroundStyle} className={classNames.join(' ')}>
      {isBackgroundVideo && backgroundImageUrl && backgroundAnimationUrl && (
        <>
          <VideoBanner id={id} mp4VideoUrl={backgroundAnimationUrl} className={styles['venue-background-video']} />
          <button className={buttonClassNames} onClick={(event) => playPauseVideo(event)}>
            {id === activeVideo ? <PauseButton /> : <PlayButton />}
          </button>
        </>
      )}
      {children}
    </div>
  );
};

export default VenueBackground;
